import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { FieldRenderProps } from "react-final-form";
import Autocomplete from "@material-ui/lab/Autocomplete";
import TextField from "@material-ui/core/TextField";
import { ForeignKeyFieldOptions, LoggerFactory } from "../../../index";
import { createStyles, makeStyles } from "@material-ui/core";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import includes from "lodash/includes";
// @ts-ignore
import SanitizedHTML from "react-sanitized-html";
import { removeTags } from "../../../helpers/htmlUtils";
import { useTranslation } from "react-i18next";

export interface SelectWithAutocompleteProps
  extends ForeignKeyFieldOptions,
    FieldRenderProps<any> {
  options: any[];
  canAddItem?: boolean;
  readOnly?: boolean;
}

const useStyles = makeStyles(
  createStyles({
    inputBase: {
      marginTop: 4,
      padding: "3.5px !important"
    },
    paper: {
      width: "max-content",
      minWidth: "100%"
    }
  })
);

interface SelectOption {}
type SelectValue = SelectOption[];

export const MultiSelectFieldWithAutocomplete = observer(
  (props: SelectWithAutocompleteProps) => {
    const {
      canAddItem,
      dataSource,
      options,
      meta,
      labelField,
      searchField,
      valueField,
      placeholder,
      renderSelectedOptions,
      clearable,
      input: { value, onFocus, onBlur },
      readOnly,
      minWidth
    } = props;

    const classes = useStyles();
    const i18n = useTranslation();
    const log = LoggerFactory.create(
      `MultiSelectWithAutocomplete [${dataSource.name}]`
    );
    const [val, setVal] = useState([] as SelectValue);

    if (labelField instanceof Function && !searchField) {
      throw new Error("searchField is mandatory if labelField is a function");
    }

    const defaultRenderSelectedOptions = (option: SelectOption): string => {
      return removeTags(getOptionLabel(option));
    };

    useEffect((): void => {
      if (isNil(value) || isEmpty(value)) {
        setVal([]);
        log.debug("value is null or empty so I am setting val to []", props);
        return;
      }

      let found = [];
      for (const element of dataSource.list) {
        if (includes(value, element[valueField])) {
          found.push(element);
        }
      }

      if (found.length !== 0) {
        setVal(found);
      } else if (dataSource.list.length > 0) {
        log.warn("did not find matching option", value);
      }
    }, [dataSource.list, value]);

    const handleChange = (event: object, vals: SelectValue): void => {
      props.input.onChange(vals.map(it => it[valueField]));
    };

    const getOptionLabel = (option: SelectOption): string => {
      return labelField instanceof Function
        ? labelField(option)
        : option[labelField];
    };

    // @ts-ignore
    const renderOptions = (option: SelectOption): string | JSX => {
      return <SanitizedHTML html={getOptionLabel(option)} />;
    };

    return (
      <Autocomplete
        style={{ minWidth: minWidth || 150 }}
        classes={{
          inputRoot: classes.inputBase,
          paper: classes.paper
        }}
        multiple={true}
        options={options}
        onOpen={event => {
          // @ts-ignore
          onFocus(event);
        }}
        onClose={event => {
          // @ts-ignore
          onBlur(event);
        }}
        disabled={readOnly}
        disableClearable={!clearable}
        getOptionLabel={
          renderSelectedOptions
            ? renderSelectedOptions
            : defaultRenderSelectedOptions
        }
        renderOption={renderOptions}
        value={val}
        freeSolo={canAddItem}
        onChange={handleChange}
        size="small"
        renderInput={params => (
          <TextField
            {...params}
            helperText={
              meta.visited || meta.touched
                ? meta.error || meta.submitError
                : undefined
            }
            error={(meta.error || meta.submitError) && meta.touched}
            variant="outlined"
            placeholder={placeholder || i18n.t("lego:select", "Select")}
            fullWidth
          />
        )}
      />
    );
  }
);
